import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid2 as Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Avatar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Bar,
  LineChart,
  Line,
  BarChart,
} from "recharts";
import { formatNumberWithSuffix } from "../../../../utils/functions";
import iSymbol from "../../../Assets/Img/iSymbol.svg";
import imagechart from "../../../Assets/Img/image follower chart.svg";
import reelchart from "../../../Assets/Img/reel follower chart.svg";
import CustomLineChart from "../../../Common/Components/Charts/LineChart";
import CustomPieChart from "../../../Common/Components/Charts/PieChart";
import FollowersGraph from "./FollowerGraph";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useEffect, useState } from "react";
import lv from "../../../Assets/Img/lv.svg";

const formatDate = (isoDate) => {
  if (!isoDate) return "";

  const posted = new Date(isoDate);
  const day = posted.getDate();
  const month = posted
    .toLocaleString("default", { month: "short" })
    .toUpperCase(); // Get the short month name (e.g., "APR")
  const year = posted.getFullYear().toString().slice(-2); // Get the last two digits of the year

  return `${day} ${month} ${year}`;
};

// Helper to extract YouTube video ID from URL
const extractYouTubeId = (url) => {
  const match = url.match(/v=([a-zA-Z0-9_-]+)/);
  return match ? match[1] : "";
};

const getRecentVideos = (stats) => {
  return (
    stats?.latest_videos?.slice(0, 4).map((video, index) => {
      const videoId = extractYouTubeId(video.video_link);
      return {
        id: index,
        title: video.video_title || `Video ${index + 1}`,
        thumbnail: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
        video_link: video.video_link,
        videoId,
        views: formatNumberWithSuffix(video.views || 0),
        likes: formatNumberWithSuffix(video.likes || 0),
        comments: formatNumberWithSuffix(video.comments || 0),
        viewsDisplay: formatNumberWithSuffix(video.views || 0),
        date: formatDate(video.post_date),
      };
    }) || []
  );
};

const getMostPopularPosts = (stats) => {
  return (
    stats?.top_liked_post_data?.slice(0, 4).map((post, index) => {
      return {
        id: index,
        title: post.caption || `Post ${index + 1}`,
        thumbnail: post.media_url || "", // Instagram image
        post_link: post.permalink || "#",
        views: formatNumberWithSuffix(post.views || 0),
        likes: formatNumberWithSuffix(post.like_count || 0),
        comments: formatNumberWithSuffix(post.comments_count || 0),
        viewsDisplay: formatNumberWithSuffix(post.views || 0),
        date: formatDate(post.timestamp),
      };
    }) || []
  );
};

const getInstagramStats = (stats) => {
  return [
    {
      label: "Followers",
      value: stats?.followers ? formatNumberWithSuffix(stats?.followers) : 0,
    },
    {
      label: "Reach",
      value: stats?.reach ? formatNumberWithSuffix(stats?.reach) : "N/A",
    },
    {
      label: "Engagement",
      value: stats?.engagement
        ? formatNumberWithSuffix(stats?.engagement) + "%"
        : "N/A",
    },
    {
      label: "Avg. Likes",
      value: stats?.avg_likes ? formatNumberWithSuffix(stats?.avg_likes) : 0,
    },
    {
      label: "Avg. Comments",
      value: stats?.avg_comments
        ? formatNumberWithSuffix(stats?.avg_comments)
        : 0,
    },
    {
      label: "Avg. Shares",
      value: stats?.avg_shares ? formatNumberWithSuffix(stats?.avg_shares) : 0,
    },
  ];
};

const cities = [
  { label: "Surat", value: 10 },
  { label: "Ahmedabad", value: 30 },
  { label: "Rajkot", value: 40 },
  { label: "Gandhinagar", value: 50 },
];

const countries = [
  { label: "India", value: 10 },
  { label: "Singapore", value: 30 },
  { label: "UK", value: 40 },
  { label: "USA", value: 50 },
  { label: "China", value: 50 },
  { label: "Japan", value: 50 },
];

const followersData = (data = []) => {
  return [
    {
      name: "0-500 accounts",
      value: +data?.find((i) => i.name === "0_500")?.percent?.toFixed(2) || 0,
      fill: "#DCE8DA",
    },
    {
      name: "500-1k accounts",
      value:
        +data?.find((i) => i.name === "500_1000")?.percent?.toFixed(2) || 0,
      fill: "#F7F3E9",
    },
    {
      name: "1k-1.5k accounts",
      value:
        +data?.find((i) => i.name === "1000_1500")?.percent?.toFixed(2) || 0,
      fill: "#EBE1D9",
    },
    {
      name: "1.5k+ accounts",
      value:
        +data?.find((i) => i.name === "15000_plus")?.percent?.toFixed(2) || 0,
      fill: "#E3E8F0",
    },
  ];
};

const ages = (data = []) => {
  return data && Array.isArray(data)
    ? data.map((i) => {
        return {
          label: i.name.replace("_", "-"), // Replace underscore with hyphen for age range
          value: i.percent.toFixed(2),
        };
      })
    : [];
};

// const data = [
//   { name: "January 23", followers: 40 },
//   { name: "February 23", followers: 35 },
//   { name: "March 23", followers: 28 },
//   { name: "April 23", followers: 25 },
//   { name: "May 23", followers: 27 },
//   { name: "June 23", followers: 34 },
//   { name: "July 23", followers: 38 },
//   { name: "August 23", followers: 40 },
//   { name: "September 23", followers: 45 },
//   { name: "October 23", followers: 48 },
//   { name: "November 23", followers: 46 },
//   { name: "December 23", followers: 30 },
//   { name: "January 24", followers: 25 },
// ];

const hashtags = [
  "FashionStyle",
  "ArtOfTheDay",
  "FashionBrand",
  "ArtistOnInstagram",
  "StreetStyle",
  "Artwork",
  "TrendyLooks",
  "ArtCollector",
  "Fashionista",
  "AbstractArt",
  "InstaFashion",
  "InstaArt",
];

const postImages = [
  "https://images.pexels.com/photos/13277976/pexels-photo-13277976.jpeg?auto=compress&cs=tinysrgb&w=1200&lazy=load",
  "https://images.pexels.com/photos/28802131/pexels-photo-28802131/free-photo-of-young-woman-hiking-by-mountain-lake.jpeg?auto=compress&cs=tinysrgb&w=1200&lazy=loada",
  "https://images.pexels.com/photos/31416362/pexels-photo-31416362/free-photo-of-dusk-skyline-of-minato-city-tokyo.jpeg?auto=compress&cs=tinysrgb&w=1200&lazy=load",
  "https://images.pexels.com/photos/9471670/pexels-photo-9471670.jpeg?auto=compress&cs=tinysrgb&w=1200&lazy=load",
];

export const Platforms = ({ details }) => {
  // collaboration with data
  const [CollaborateWith, setCollaborateWith] = useState(
    details?.pastCollaboratedBrandData || []
  );

  useEffect(() => {
    setCollaborateWith(details?.pastCollaboratedBrandData || []);
  }, [details]);

  const statsOverview = getInstagramStats({
    ...(details.instagram_stats || {}),
    engagement: details?.engagement || 0,
  });

  // gender Wise Followers data
  const genderData = (data) => {
    // Default to 0 if values are not available
    const malePercent = data?.male || 0;
    const femalePercent = data?.female || 0;

    // Calculate other gender categories (if needed)
    const otherPercent = 100 - malePercent - femalePercent;

    return [
      {
        name: "Male",
        value: malePercent,
        fill: "rgba(107, 142, 118, 1)",
      },
      {
        name: "Female",
        value: femalePercent,
        fill: "rgba(182, 215, 185, 1)",
      },
      {
        name: "Other",
        value: otherPercent > 0 ? otherPercent : 0, // Ensures no negative percentages
        fill: "rgba(151, 183, 154, 1)",
      },
    ];
  };
  const formatNumberWithSuffix = (num, isFollowers = false) => {
    if (num === null || num === undefined || isNaN(num)) return "N/A";
    if (isFollowers) return Math.round(num).toString(); // Round followers value
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(2) + "M";
    if (num >= 1_000) return (num / 1_000).toFixed(2) + "K";
    return num.toFixed(2);
  };

  const getSocialPlatforms = (stats) => {
    return [
      {
        label: "Followers",
        value: stats?.followers
          ? formatNumberWithSuffix(stats?.followers, true)
          : "",
      },
      {
        label: "Reach",
        value: stats?.reach ? formatNumberWithSuffix(stats?.reach) : "N/A",
      },
      {
        label: "Engagement",
        value: stats?.engagement
          ? formatNumberWithSuffix(stats?.engagement) + "%"
          : "N/A",
      },
      {
        label: "Avg. Likes",
        value: stats?.avg_likes
          ? formatNumberWithSuffix(stats?.avg_likes)
          : "0.00",
      },
      {
        label: "Avg. Comments",
        value: stats?.avg_comments
          ? formatNumberWithSuffix(stats?.avg_comments)
          : "0.00",
      },
      {
        label: "Avg. Shares",
        value: stats?.avg_shares
          ? formatNumberWithSuffix(stats?.avg_shares)
          : "0.00",
      },
    ];
  };
  const recentVideos = getRecentVideos({
    ...(details.youtube_stats || {}),
    engagement: details?.engagement || 0,
  });
  const topLikedPostData = getMostPopularPosts({
    ...(details.youtube_stats || {}),
    engagement: details?.engagement || 0,
  });
  const mostPopularPosts = getMostPopularPosts(details?.instagram_stats || {});

  const [timeRange, setTimeRange] = useState("all");
  const handleTimeRangeChange = (event, newTimeRange) => {
    if (newTimeRange !== null) {
      setTimeRange(newTimeRange);
    }
  };
  // 1. Flatten the entire data array
  const postArray = Array.isArray(details?.instagram_stats?.latest_post_data)
    ? details.instagram_stats.latest_post_data.flat()
    : [];

  // 2. Extract all post timestamps
  const allPostDates = postArray
    .map((post) => Number(post?.post_creation_at))
    .filter(Boolean);

  // 3. Create a Set of unique month-year labels from the post dates
  const monthSet = new Set();
  allPostDates.forEach((timestamp) => {
    const date = new Date(timestamp);
    const label = date.toLocaleDateString("en-US", {
      month: "short",
      year: "2-digit",
    });
    monthSet.add(label);
  });

  // 4. Sort the months chronologically
  const sortedMonths = Array.from(monthSet).sort((a, b) => {
    const [aMonth, aYear] = a.split(" ");
    const [bMonth, bYear] = b.split(" ");
    const dateA = new Date(`20${aYear}`, new Date(`${aMonth} 1`).getMonth());
    const dateB = new Date(`20${bYear}`, new Date(`${bMonth} 1`).getMonth());
    return dateA - dateB;
  });

  // 5. Count posts per month
  const postCountMap = {};
  postArray.forEach((post) => {
    const timestamp = Number(post?.post_creation_at);
    if (!timestamp) return;
    const date = new Date(timestamp);
    const label = date.toLocaleDateString("en-US", {
      month: "short",
      year: "2-digit",
    });
    postCountMap[label] = (postCountMap[label] || 0) + 1;
  });

  // 6. Helper to get Date object from label like "Jan '25"
  const getDateFromLabel = (label) => {
    const [monthStr, yearStr] = label.split(" ");
    const month = new Date(`${monthStr} 1`).getMonth();
    const year = Number(`20${yearStr}`);
    return new Date(year, month, 1);
  };

  // 7. Filter sorted months based on selected time range
  let filteredMonths = [...sortedMonths];

  if (timeRange !== "all") {
    const now = new Date();
    let monthsAgo = 0;
    if (timeRange === "1y") monthsAgo = 12;
    else if (timeRange === "6m") monthsAgo = 6;
    else if (timeRange === "3m") monthsAgo = 3;

    const cutoffDate = new Date(
      now.getFullYear(),
      now.getMonth() - monthsAgo + 1,
      1
    );
    filteredMonths = sortedMonths.filter(
      (label) => getDateFromLabel(label) >= cutoffDate
    );
  }

  // 8. Prepare chart data
  const data = filteredMonths.map((label) => ({
    name: label,
    posts: postCountMap[label] || 0,
    // hasRealPosts: !!postCountMap[label],
  }));

  const collaborators = [
    {
      name: "Nykaa",
      img: lv,
    },
    {
      name: "John Morgan",
      img: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      name: "Chanel",
      img: lv,
    },
    {
      name: "Gucci",
      img: "https://upload.wikimedia.org/wikipedia/commons/3/38/Gucci_Logo.svg",
    },
    {
      name: "Louis Vuitton",
      img: lv,
    },
    {
      name: "Alex Volkov",
      img: lv,
    },
    {
      name: "Kiara Sharma",
      img: "https://randomuser.me/api/portraits/women/44.jpg",
    },
    {
      name: "Rhys Larsen",
      img: lv,
    },
    {
      name: "H&M",
      img: "https://upload.wikimedia.org/wikipedia/commons/5/53/H%26M-Logo.svg",
    },
  ];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box>
      <Grid container spacing={4}>
        <Grid size={12}>
          <Box
            sx={{
              width: "100%",
              mx: "",
              p: 3,
              backgroundColor: "white",
              borderRadius: 2,
              border: "1px solid rgba(238, 238, 238, 1)",
            }}
          >
            <Typography className="heading text-start">Overview</Typography>
            <Grid container spacing={2}>
              {statsOverview.map((stat, index) => {
                return (
                  <Grid key={index} size={2}>
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      flexDirection={"column"}
                      sx={{
                        background: "#fff",
                        padding: "1rem",
                        borderRadius: "8px",
                        border: "1px solid rgba(238, 238, 238, 1)",
                      }}
                    >
                      <Typography
                        fontSize={"18px"}
                        fontWeight={600}
                        color="#676767"
                      >
                        {stat.value}
                      </Typography>
                      <Typography color="#676767" fontSize={"14px"}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Grid>

        {/* genuine_followers */}
        <Box
          sx={{
            width: "100%",
            mx: "",
            p: 3,
            backgroundColor: "white",
            borderRadius: 2,
            border: "1px solid rgba(238, 238, 238, 1)",
          }}
        >
          <Typography className="heading text-start" mb={2}>
            Followers Number Breakup
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              mt: 2, // Add margin-top to push the entire Box downward
            }}
          >
            {/* Donut Chart */}
            <Box
              sx={{
                position: "relative",
                width: "120px",
                height: "120px",
                mx: "auto",
                mt: 4, // Add margin-top to push it downward
              }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                {/* Background circle */}
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#f0f0f0"
                  strokeWidth="20"
                />
                {/* Progress circle - 80% */}
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#4ade80"
                  strokeWidth="20"
                  strokeDasharray="251.2"
                  strokeDashoffset="50.24"
                  transform="rotate(-90 50 50)"
                />
              </svg>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{ fontSize: "0.975rem", fontWeight: "500" }}
                  variant="h6"
                  // fontWeight="bold"
                  color="green"
                >
                  {formatNumberWithSuffix(
                    details?.instagram_stats?.genuine_followers
                  )}
                  %
                </Typography>
                <Typography variant="body2" color="green">
                  Genuine
                </Typography>
              </Box>
            </Box>

            {/* Metrics Cards */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                flexGrow: 1,
              }}
            >
              {/* Images Card */}
              <Box
                sx={{
                  flex: 1,
                  border: "1px solid rgba(238, 238, 238, 1)",
                  borderRadius: 2,
                }}
              >
                {" "}
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <img src={imagechart} alt="info" width={16} height={16} />

                    <Typography
                      variant="body2"
                      color="textSecondary"
                      fontWeight="bold"
                      sx={{ textTransform: "uppercase" }}
                    >
                      Images
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <Typography variant="h6" color="green">
                      {details?.engagement != null && !isNaN(details.engagement)
                        ? Math.round(details.engagement)
                        : "N/A"}
                      %
                    </Typography>

                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
                    >
                      Engagement
                    </Typography>
                    <img src={iSymbol} alt="info" width={16} height={16} />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="h6" color="green">
                      {formatNumberWithSuffix(
                        details?.instagram_stats?.avg_likes || "N/A"
                      )}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
                    >
                      Avg.Likes
                    </Typography>
                    <img src={iSymbol} alt="info" width={16} height={16} />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="h6" color="green">
                      {formatNumberWithSuffix(
                        details?.instagram_stats?.avg_comments || "N/A"
                      )}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
                    >
                      Avg. Comment
                    </Typography>
                    <img src={iSymbol} alt="info" width={16} height={16} />
                  </Box>
                </CardContent>
              </Box>

              {/* Reels Card */}
              <Box
                sx={{
                  flex: 1,
                  border: "1px solid rgba(238, 238, 238, 1)",
                  borderRadius: 2,
                }}
              >
                {" "}
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {/* <Movie sx={{ width: 20, height: 20 }} /> */}
                    <img src={reelchart} alt="info" width={16} height={16} />

                    <Typography
                      variant="body2"
                      color="textSecondary"
                      fontWeight="bold"
                      sx={{ textTransform: "uppercase" }}
                    >
                      Reels
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <Typography variant="h6" color="green">
                      {details?.engagement != null && !isNaN(details.engagement)
                        ? Math.round(details.engagement)
                        : "N/A"}
                      %
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
                    >
                      Engagement
                    </Typography>
                    <img src={iSymbol} alt="info" width={16} height={16} />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="h6" color="green">
                      {formatNumberWithSuffix(
                        details?.instagram_stats?.avg_likes || "N/A"
                      )}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
                    >
                      Avg.Likes
                    </Typography>
                    <img src={iSymbol} alt="info" width={16} height={16} />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="h6" color="green">
                      {formatNumberWithSuffix(
                        details?.instagram_stats?.avg_comments || "N/A"
                      )}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
                    >
                      Avg. Comment
                    </Typography>
                    <img src={iSymbol} alt="info" width={16} height={16} />
                  </Box>
                </CardContent>
              </Box>
            </Box>
          </Box>
        </Box>

        <Grid size={12} sx={{}}>
          <FollowersGraph />
        </Grid>
        <Grid size={12}>
          <Box
            sx={{
              width: "100%",
              mx: "",
              p: 3,
              backgroundColor: "white",
              borderRadius: 2,
              border: "1px solid rgba(238, 238, 238, 1)",
            }}
          >
            <Typography className="heading text-start mb-3">
              Audience Details By Subscribers{" "}
            </Typography>
            <Grid container spacing={4} sx={{ height: "100%" }}>
              <Grid size={6}>
                <Box
                  sx={{
                    background: "#FFFFFF",
                    padding: "1rem 1rem 2rem",
                    borderRadius: "8px",
                    height: "100%",
                    border: "1px solid rgba(238, 238, 238, 1)",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#676767",
                      "& span": {
                        fontSize: "0.6rem",
                        fontWeight: 400,
                      },
                    }}
                  >
                    Cities <span>(Top 5)</span>
                  </Typography>
                  <Box mt={2} display={"flex"} flexDirection={"column"} gap={3}>
                    {details.instagram_stats?.top_cities?.length > 0 ? (
                      details.instagram_stats?.top_cities.map((i) => {
                        return (
                          <Box>
                            <Box
                              display={"flex"}
                              justifyContent={"space-between"}
                            >
                              <Typography fontSize={"14px"}>
                                {i.name}
                              </Typography>
                              <Typography fontSize={"14px"}>
                                {i.percent.toFixed(2)} %
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                height: "8px",
                                borderRadius: "4px",
                                background: "#67676733",
                                marginTop: "6px",
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${i.percent}%`,
                                  height: "8px",
                                  borderRadius: "4px",
                                  background: "#6B8E76",
                                }}
                              />
                            </Box>
                          </Box>
                        );
                      })
                    ) : (
                      <Box
                        sx={{
                          minHeight: "240px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        No Data Available
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid size={6}>
                <Box
                  sx={{
                    background: "#FFFFFF",
                    padding: "1rem 1rem 2rem",
                    borderRadius: "8px",
                    height: "100%",
                    border: "1px solid rgba(238, 238, 238, 1)",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#676767",
                      "& span": {
                        fontSize: "0.6rem",
                        fontWeight: 400,
                      },
                    }}
                  >
                    Countries <span>(Top 5)</span>
                  </Typography>
                  <Box mt={2} display={"flex"} flexDirection={"column"} gap={3}>
                    {details?.instagram_stats?.top_countries?.length > 0 ? (
                      details?.instagram_stats?.top_countries.map((i) => {
                        return (
                          <Box>
                            <Box
                              display={"flex"}
                              justifyContent={"space-between"}
                            >
                              <Typography fontSize={"14px"}>
                                {i.name}
                              </Typography>
                              <Typography fontSize={"14px"}>
                                {i.percent?.toFixed(2)} %
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                height: "8px",
                                borderRadius: "4px",
                                background: "#67676733",
                                marginTop: "6px",
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${i.percent}%`,
                                  height: "8px",
                                  borderRadius: "4px",
                                  background: "#6B8E76",
                                }}
                              />
                            </Box>
                          </Box>
                        );
                      })
                    ) : (
                      <Box
                        sx={{
                          minHeight: "240px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        No Data Available
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid size={6}>
                <Box
                  sx={{
                    background: "#FFFFFF",
                    padding: "1rem 1rem 2rem",
                    borderRadius: "8px",
                    height: "100%",
                    border: "1px solid rgba(238, 238, 238, 1)",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#676767",
                      "& span": {
                        fontSize: "0.6rem",
                        fontWeight: 400,
                      },
                    }}
                  >
                    Age
                  </Typography>
                  <Box
                    mt={2}
                    display={"flex"}
                    flexDirection={"column"}
                    gap={3}
                    height={"100%"}
                  >
                    {ages(details?.instagram_stats?.age_groups || [])?.length >
                    0 ? (
                      ages(details?.instagram_stats?.age_groups || []).map(
                        (i) => {
                          return (
                            <Box>
                              <Box
                                display={"flex"}
                                justifyContent={"space-between"}
                              >
                                <Typography fontSize={"14px"}>
                                  {i.label}
                                </Typography>
                                <Typography fontSize={"14px"}>
                                  {i.value} %
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  height: "8px",
                                  borderRadius: "4px",
                                  background: "#67676733",
                                  marginTop: "6px",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${i.value}%`,
                                    height: "8px",
                                    borderRadius: "4px",
                                    background: "#6B8E76",
                                  }}
                                />
                              </Box>
                            </Box>
                          );
                        }
                      )
                    ) : (
                      <Box
                        sx={{
                          minHeight: "240px",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        No Data Available
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid size={6}>
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={2}
                  height={"100%"}
                >
                  <Box
                    height={"100%"}
                    sx={{
                      border: "1px solid rgba(238, 238, 238, 1)",
                      p: 2,
                      borderRadius: "4px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#676767",
                        "& span": {
                          fontSize: "0.6rem",
                          fontWeight: 400,
                        },
                      }}
                    >
                      Gender
                    </Typography>
                    {(() => {
                      const gender = genderData(details); // Use the details object directly
                      const isGenderDataExists =
                        gender.filter((i) => i.value !== 0)?.length > 0;

                      return (
                        <Box
                          sx={{
                            background: "#fff",
                            display: "flex",
                            alignItems: "stretch",
                            flexDirection: "column",
                            borderRadius: "8px",
                            padding: "1rem",
                            gap: "1rem",
                          }}
                        >
                          {isGenderDataExists ? (
                            <>
                              <Box display="flex" justifyContent="center">
                                <CustomPieChart
                                  width={280}
                                  height={280}
                                  data={gender}
                                  innerRadius={60}
                                  customLabel={false}
                                />
                              </Box>

                              <Box
                                display="flex"
                                flexDirection="column"
                                gap={1}
                                width="100%"
                                // maxWidth="300px"
                              >
                                {gender.map((i) => (
                                  <Box
                                    key={i.name}
                                    display="flex"
                                    alignItems="center"
                                    gap={3}
                                    color="#6B4750"
                                    px={2}
                                    py={1}
                                    sx={{
                                      border: "1px solid #e0e0e0",
                                      borderRadius: "50px",
                                    }}
                                  >
                                    <Box
                                      display="flex"
                                      gap={2}
                                      alignItems="center"

                                      // flexGrow={1}
                                    >
                                      <Box
                                        width="16px"
                                        height="16px"
                                        sx={{
                                          background: i.fill,
                                          borderRadius: "50%",
                                        }}
                                      />
                                      <Typography fontSize="12px">
                                        {i.name} -
                                      </Typography>
                                    </Box>

                                    <Typography
                                      fontSize="12px"
                                      sx={{ fontWeight: "bold" }}
                                    >
                                      <Box fontWeight="bold">
                                        {i.value.toFixed(2)}%
                                      </Box>{" "}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </>
                          ) : (
                            <Box
                              sx={{
                                width: "100%",
                                minHeight: "240px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              No Data Available
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>
                  {/* Languagesssss */}
                  {/* <Box
                    sx={{
                      display: "flex",
                      flex: 1,
                      background: "#FFFFFF",
                      padding: "1rem 1rem 2rem",
                      // borderRadius: "8px",
                      height: "100%",

                      border: "1px solid rgba(238, 238, 238, 1)",
                      p: 2,
                      borderRadius: "4px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#676767",
                        "& span": {
                          fontSize: "0.6rem",
                          fontWeight: 400,
                        },
                      }}
                    >
                      Languages
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        minHeight: "240px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      No Data Available
                    </Box>
                  </Box> */}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        {/* hashtags */}
        <Grid
          size={12}
          sx={{
            border: "1px solid rgba(238, 238, 238, 1)",
            borderRadius: "4px",
            p: 3,
          }}
        >
          <Box height={"100%"} display={"flex"} flexDirection={"column"}>
            <Typography className="heading text-start">
              Most Used Hashtags{" "}
            </Typography>
            <Box
              sx={{
                background: "#fff",
                display: "flex",
                borderRadius: "8px",
                padding: "1rem",
                rowGap: "3px",
                color: "#676767",
                height: "150px",
              }}
            >
              {(() => {
                const hashtags = details?.instagram_stats?.top_liked_post_data
                  ?.map((i) => i.hashtags)
                  .flat(2)
                  .filter(Boolean);

                return hashtags?.length ? (
                  <Grid container columnSpacing={2}>
                    {hashtags.slice(0, 10).map((i) => {
                      return (
                        <Grid size={6}>
                          <Tooltip title={`#${i}`}>
                            <Typography
                              fontSize={"14px"}
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              #{i}
                            </Typography>
                          </Tooltip>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      minHeight: "150px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    No Data Available
                  </Box>
                );
              })()}
            </Box>
          </Box>
        </Grid>
        <Grid size={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: "1px solid rgba(238, 238, 238, 1)",
              maxWidth: "100%",
              marginTop: "10px",
            }}
          >
            <Box
              sx={
                {
                  // border: "1px solid rgba(238, 238, 238, 1)",
                  // borderRadius: "4px",
                  // p: 3,
                }
              }
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography className="heading text-start">
                  Account Activity{" "}
                  <span style={{ fontWeight: 400 }}>
                    ( No. of Post per Month )
                  </span>
                </Typography>

                <ToggleButtonGroup
                  value={timeRange}
                  exclusive
                  onChange={handleTimeRangeChange}
                  aria-label="time range"
                  size="small"
                  sx={{
                    "& .MuiToggleButton-root": {
                      borderRadius: 4,
                      mx: 0.5, // space between buttons
                      px: 2,
                      py: 0.5,
                      color: "text.secondary",
                      backgroundColor: " rgba(247, 242, 250, 1)",
                      border: "1px solid transparent", // remove default border
                      "&.Mui-selected": {
                        backgroundColor: "transparent", // no bg color
                        color: "text.primary",
                        fontWeight: "medium",
                        boxShadow: " 0px 1px 3px 1px rgba(0, 0, 0, 0.15)",
                      },
                      "&:hover": {
                        backgroundColor: " rgba(247, 242, 250, 1)",
                      },
                    },
                  }}
                >
                  <ToggleButton value="all" aria-label="all time">
                    All
                  </ToggleButton>
                  <ToggleButton value="1y" aria-label="1 year">
                    1Y
                  </ToggleButton>
                  <ToggleButton value="6m" aria-label="6 months">
                    6M
                  </ToggleButton>
                  <ToggleButton value="3m" aria-label="3 months">
                    3M
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {data?.length <= 0 ? (
                <Box
                  sx={{
                    minHeight: "240px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  No Data Available
                </Box>
              ) : (
                <Box
                  sx={{
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "8px",
                    paddingY: "1rem",
                    paddingX: "1rem",
                    width: "100%",
                    height: 400,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={data}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis
                        domain={[0, "dataMax + 1"]}
                        ticks={Array.from(
                          { length: Math.max(...data.map((d) => d.posts)) + 2 },
                          (_, i) => i
                        )}
                        allowDecimals={false}
                      />
                      <Tooltip
                        formatter={(value, name) => [`${value} posts`, name]}
                        labelFormatter={(label) => `Posts from ${label}`}
                      />
                      <Line
                        type="monotone"
                        dataKey="posts"
                        stroke="#6B8E76"
                        strokeWidth={2}
                        dot={({ cx, cy, payload }) =>
                          payload.hasRealPosts ? (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={4}
                              stroke="#6B8E76"
                              fill="#6B8E76"
                            />
                          ) : null
                        }
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Collaboration with */}
        <Box
          sx={{
            borderRadius: 2,
            p: 2,
            border: "1px solid rgba(238, 238, 238, 1)",
            width: "100%",
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Collaborate With
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: 3, sm: 4 },
                justifyContent: { xs: "center", sm: "space-between" },
              }}
            >
              {CollaborateWith.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: { xs: 3, sm: 4 },
                    justifyContent: { xs: "center", sm: "space-between" },
                  }}
                >
                  {CollaborateWith.map((item, idx) => (
                    <Stack
                      key={idx}
                      spacing={1}
                      alignItems="center"
                      sx={{ width: 80, textAlign: "center" }}
                    >
                      <Avatar
                        alt={item.brand_name || "Unknown Brand"}
                        src={item.profile || lv}
                        sx={{ width: 50, height: 50, bgcolor: "#f5f5f5" }}
                        variant="circular"
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: 12,
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          maxWidth: "100%",
                        }}
                      >
                        {item.brand_name || "N/A"}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    minHeight: "100px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No Collaborations Available
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Box>
        {/* Audience Reachability*/}
        {/* <Grid
          size={7}
          sx={{
            border: "1px solid rgba(238, 238, 238, 1)",
            borderRadius: "4px",
            p: 3,
          }}
        >
          <Box height={"100%"}>
            <Typography
              color="primary"
              fontWeight={500}
              fontSize={"20px"}
              paddingBottom={"0.6rem"}
            >
              Audience Reachability
            </Typography>
            {(() => {
              const reachability = followersData(
                details?.instagram_stats?.reachability || []
              );
              console.log(reachability);
              const isDataExists =
                reachability.filter((i) => i.value !== 0).length > 0;

              return (
                <Box
                  sx={{
                    background: "#fff",
                    display: "flex",
                    alignItems: "stretch",
                    borderRadius: "8px",
                    padding: "1rem",
                    gap: "1rem",
                  }}
                >
                  {isDataExists ? (
                    <>
                      <Box
                        display="flex"
                        flex={1}
                        flexDirection="column"
                        justifyContent="space-between"
                        sx={{ overflow: "hidden" }} // optional, if text overflows
                      >
                        <Typography color="#676767" fontSize={"14px"}>
                          How many accounts do @cardiB's followers follow?
                        </Typography>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                          {reachability.map((i) => {
                            return (
                              <Box
                                display={"flex"}
                                justifyContent={"space-between"}
                                alignItems={"center"}
                                color={"#6B4750"}
                              >
                                <Box
                                  display={"flex"}
                                  gap={2}
                                  alignItems={"center"}
                                >
                                  <Box
                                    width={"16px"}
                                    height={"16px"}
                                    sx={{ background: i.fill }}
                                  />
                                  <Typography fontSize={"12px"}>
                                    {i.name}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography
                                    fontSize={"12px"}
                                    textAlign={"end"}
                                  >
                                    {i.value} %
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                      <Box
                        display="flex"
                        flex={1}
                        alignItems={"center"}
                        height="100%"
                        justifyContent="center"
                        sx={{ overflow: "hidden" }} // optional, if text overflows
                      >
                        <CustomPieChart
                          width={200}
                          height={200}
                          data={reachability}
                          innerRadius={60}
                          customLabel={false}
                        />
                      </Box>
                    </>
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        minHeight: "240px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      No Data Available
                    </Box>
                  )}
                </Box>
              );
            })()}
          </Box>
        </Grid> */}

        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            border: "1px solid rgba(238, 238, 238, 1)",
            borderRadius: "8px",
            mt: "10px",
            padding: "10px",
          }}
        >
          {/* recent videos */}
          <Box
            sx={{
              width: "100%",
              border: "1px solid rgba(238, 238, 238, 1)",
              p: 1,
              borderRadius: "8px",
              mt: "10px",
            }}
          >
            <Typography className="heading text-start mb-2">
              Latest Post{" "}
            </Typography>

            <Grid container spacing={0.5}>
              {recentVideos.slice(0, 4).map((video) => (
                <Grid item xs={12} sm={6} md={3} key={video.id}>
                  <Card
                    sx={{
                      borderRadius: 1,
                      boxShadow: "none",
                      height: "100%",
                      width: "100%",
                      maxWidth: "240px",
                      display: "flex",
                      flexDirection: "column",
                      bgcolor: "transparent",
                    }}
                  >
                    <Box sx={{ position: "relative" }}>
                      <CardMedia
                        component="img"
                        height="180"
                        image={video?.thumbnail}
                        alt={video?.title}
                        sx={{
                          borderRadius: 1,
                          filter: "brightness(0.85)",
                        }}
                      />
                      <IconButton
                        sx={{
                          position: "absolute",
                          top: "20px",
                          right: "8px",
                          height: "28px",
                          width: "28px",
                          color: "white",
                          bgcolor: "rgba(0,0,0,0.3)",
                          "&:hover": {
                            bgcolor: "rgba(0,0,0,0.5)",
                          },
                        }}
                        onClick={() => window.open(video.video_link, "_blank")}
                      >
                        <PlayArrowIcon />
                      </IconButton>

                      {video.viewsDisplay && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            width: "100%",
                            textAlign: "center",
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "1.2rem",
                            textShadow: "1px 1px 3px rgba(0,0,0,0.7)",
                            p: 1,
                            background:
                              "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                          }}
                        >
                          {/* {video.viewsDisplay} */}
                        </Box>
                      )}
                    </Box>

                    <CardContent sx={{ p: 1, pt: 1.5, pb: "8px !important" }}>
                      <Stack
                        direction="row"
                        spacing={3} // Increased spacing to make elements more spread out
                        alignItems="center"
                        sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.3,
                          }}
                        >
                          {" "}
                          {/* Increased gap */}
                          <VisibilityIcon sx={{ fontSize: "0.875rem" }} />
                          <Typography variant="caption">
                            {video?.views}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.3,
                          }}
                        >
                          {" "}
                          {/* Increased gap */}
                          <ThumbUpAltIcon sx={{ fontSize: "0.875rem" }} />
                          <Typography variant="caption">
                            {video?.likes}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.3,
                          }}
                        >
                          {" "}
                          {/* Increased gap */}
                          <ChatBubbleOutlineIcon
                            sx={{ fontSize: "0.875rem" }}
                          />
                          <Typography variant="caption">
                            {video?.comments}
                          </Typography>
                        </Box>

                        <Typography
                          variant="caption"
                          sx={{
                            ml: "auto !important",
                            color: "text.secondary",
                          }}
                        >
                          {video.date}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/*  most popular videos */}
          {details?.instagram_stats?.top_liked_post_data?.length > 0 && (
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                margin: "0",
                border: "1px solid rgba(238, 238, 238, 1)",
                p: 1,
                borderRadius: "8px",
                mt: "10px",
              }}
            >
              <Typography className="heading text-start mb-2">
                Most Popular Posts
              </Typography>

              <Grid container spacing={0.5}>
                {details?.instagram_stats?.top_liked_post_data
                  .slice(0, 4)
                  .map((post, index) => {
                    const isVideo = post?.media_type === 2 && post.videos?.[0];
                    const isImage = post?.media_type !== 2 && post.images?.[0];

                    if (!isVideo && !isImage) return null;

                    const thumbnail = isVideo ? post.videos[0] : post.images[0];

                    return (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card
                          sx={{
                            borderRadius: 1,
                            boxShadow: "none",
                            height: "100%",
                            width: "100%",
                            maxWidth: "240px",
                            display: "flex",
                            flexDirection: "column",
                            bgcolor: "transparent",
                          }}
                        >
                          <Box sx={{ position: "relative" }}>
                            {isVideo ? (
                              <video
                                controls
                                width="100%"
                                style={{
                                  borderRadius: "8px",
                                  aspectRatio: "1/1",
                                  objectFit: "cover",
                                  filter: "brightness(0.85)",
                                }}
                                controlsList="nodownload"
                                onContextMenu={(e) => e.preventDefault()}
                              >
                                <source src={thumbnail} type="video/mp4" />
                              </video>
                            ) : (
                              <CardMedia
                                component="img"
                                height="180"
                                image={thumbnail}
                                alt="Popular Post"
                                sx={{
                                  // borderRadius: 1,
                                  filter: "brightness(0.85)",
                                  aspectRatio: "1/1",
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                }}
                              />
                            )}

                            <IconButton
                              sx={{
                                position: "absolute",
                                top: "20px",
                                right: "8px",
                                height: "28px",
                                width: "28px",
                                color: "white",
                                bgcolor: "rgba(0,0,0,0.3)",
                                "&:hover": {
                                  bgcolor: "rgba(0,0,0,0.5)",
                                },
                              }}
                              onClick={() =>
                                window.open(post.post_link, "_blank")
                              }
                            >
                              <OpenInNewIcon />
                            </IconButton>
                          </Box>

                          <CardContent
                            sx={{ p: 1, pt: 1.5, pb: "8px !important" }}
                          >
                            <Stack
                              direction="row"
                              spacing={""}
                              alignItems="center"
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.75rem",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.2,
                                }}
                              >
                                <VisibilityIcon sx={{ fontSize: "0.875rem" }} />
                                <Typography variant="caption">
                                  {post.views}
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <ThumbUpAltIcon sx={{ fontSize: "0.875rem" }} />
                                <Typography variant="caption">
                                  {post.likes}
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.2,
                                }}
                              >
                                <ChatBubbleOutlineIcon
                                  sx={{ fontSize: "0.875rem" }}
                                />
                                <Typography variant="caption">
                                  {post.comments}
                                </Typography>
                              </Box>

                              <Typography
                                variant="caption"
                                sx={{
                                  ml: "auto !important",
                                  color: "text.secondary",
                                }}
                              >
                                {post.date}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
              </Grid>
            </Box>
          )}
        </Box>

        {/* {details.instagram_stats.top_liked_post_data?.length ? (
          <Grid size={12}>
            <Box height={"100%"}>
              <Typography
                color="primary"
                fontWeight={500}
                fontSize={"20px"}
                paddingBottom={"0.6rem"}
              >
                Most Popular Posts
              </Typography>
              <Box>
                <Grid container columnSpacing={2}>
                  {details?.instagram_stats?.top_liked_post_data
                    .slice(0, 4)
                    .map((i) => {
                      return (i?.media_type === 2 && i.videos[0]) ||
                        (i?.media_type !== 2 && i.images[0]) ? (
                        <Grid size={3}>
                          {i?.media_type === 2 ? (
                            <video
                              controls
                              width={"100%"}
                              style={{
                                aspectRatio: "1/1",
                                objectFit: "cover",
                                borderRadius: "8px",
                              }}
                              controlsList="nodownload"
                              oncontextmenu="return false;"
                            >
                              <source src={i.videos[0]} type="video/mp4" />
                            </video>
                          ) : (
                            <img
                              src={`${i.images[0]}`}
                              width={"100%"}
                              style={{
                                aspectRatio: "1/1",
                                objectFit: "cover",
                                borderRadius: "8px",
                              }}
                              alt=""
                              loading="lazy"
                            />
                          )}
                        </Grid>
                      ) : null;
                    })
                    .filter(Boolean)}
                </Grid>
              </Box>
            </Box>
          </Grid>
        ) : (
          <></>
        )} */}
      </Grid>
    </Box>
  );
};
